import { EmptyView } from "@eusoft/webapp-framework/components/EmptyView";
import { FieldSearchMode, FilterEditor } from "@eusoft/webapp-framework/components/FilterEditor";
import { ItemListContent } from "@eusoft/webapp-framework/content/ItemListContent";
import { formatDateTime } from "@eusoft/webapp-framework/helpers/Format";
import { dateRangeSearch } from "@eusoft/webapp-framework/helpers/SmartSearch";
import { MaterialIcon } from "@eusoft/webapp-ui/components/Icon";
import { enumItemsSource } from "@eusoft/webapp-ui/editors/ItemsSources";
import { fixEndDate, fixStartDate } from "@eusoft/webapp-ui/utils/Date";
import { formatCurrency, formatString } from "@eusoft/webapp-ui/utils/Format";
import { paymentService } from "../../abstraction/IPaymentService";
import { navigatePage } from "../../actions/Common";
import { type IOrderListView, type IListOrder, SortDirection, OrderStatus, OrderType, type IListMerchant, PaymentMethod, type IListCard } from "../../entities/Commands";
import { Icons } from "../../entities/Icons";
import { formatOrderStatus, formatOrderType } from "../../helpers/Format";
import { cardsItemsSource, entityItemsSource, merchantItemsSource } from "../../helpers/ItemsSources";
import { context } from "../../services/Context";
import { executeAsync } from "../../services/DataService";
import { WithContext } from "../../services/WithContext";
import "./ListOrderPage.scss"; 

const listOrderFilter = () => new FilterEditor<IListOrder, IOrderListView>({
    fields: {
        fromDate: {
            dataType: "date",
        },
        toDate: {
            dataType: "date",
        },
        status: {
            multipleValues: true,
            dataType: "enum",
            searchMode: FieldSearchMode.Client,
            valuesSource: enumItemsSource(OrderStatus), 
            icon: <MaterialIcon name="chronic" />,
            color: "#FF5722", // deep-orange 500
        },
        type: {
            multipleValues: true,
            dataType: "enum",
            searchMode: FieldSearchMode.Client,
            valuesSource: enumItemsSource(OrderType),
            icon: <MaterialIcon name="category" />,
            color: "#3F51B5", // indigo 500
        },
        paymentMethod: {
            multipleValues: true,
            dataType: "enum",
            label: "payment",
            searchMode: FieldSearchMode.Client,
            valuesSource: enumItemsSource(PaymentMethod),
            color: "#4CAF50", // green 500
            icon: <MaterialIcon name="money" />
        },
        salePointId: {
            label: "sale-point",
            valuesSource: entityItemsSource("ListSalePoint"),
            searchMode: FieldSearchMode.Client,
            icon: Icons["sale-point"],
            color: "#2196F3", // blue 500
        },
        paymentCardId: {
            label: "card",
            valuesSource: cardsItemsSource,
            searchMode: FieldSearchMode.Source | FieldSearchMode.LabelOnly | FieldSearchMode.MatchLabel,
            icon: Icons["payment-card"],
            minQueryLen: 1,
            pickAsync: async () => {
                const card = await paymentService.readCardAsync({
                    message: formatString("msg-wait-for-card-read")
                });
                if (card?.code) {
                    const info = await executeAsync("GetCardInfo", { cardCode: card.code });
                    if (!info?.cardId)
                        return;
                    return {
                        text: card.code,
                        value: info.cardId
                    };
                }
            },
            color: "#009688", // teal 500
            valuesFilter: (filter, query) => ({
                code: query.full,
                saleCircuitId: context.saleCircuit?.id
            } as Partial<IListCard>)
        },
        merchantId: {
            label: "merchant",
            color: "#9C27B0", // purple 500
            valuesSource: merchantItemsSource,
            searchMode: FieldSearchMode.Client,
            icon: Icons["merchant"],
            valuesFilter: a => ({
               
            } as Partial<IListMerchant>)
        },
    },
    renderMode: "field",
    matchFields: a => [a.customer.name],
    queryField: "query",
    searchProviders: [dateRangeSearch({
        fromField: "fromDate", 
        toField: "toDate",
        label: "date",
        color: "red"
    })]
});


export const ListOrderPage = ItemListContent.builder<IOrderListView, IListOrder>(true)

    .info({
        name: "order-list",
        route: "/orders/manage",
        icon: <MaterialIcon style="purple" name="list" />,
    })

    .resource("Order")

    .feature(WithContext("sale-circuit"))

    .options({

        style: ["panel", "round-item"],
        canOpen: true,
        canDelete: false,
        canEdit: false,
        canAdd: false,
        pageSize: 30,
        paginationMode: "manual",
        filterEditor: listOrderFilter,
        filterMode: "tags",


        itemsSource: {
            ...entityItemsSource("ListOrder"),
            getIcon: item => {
                return formatOrderStatus(item.status)?.icon
            }
        },

        prepareFilter: (filter, offset, limit) => ({
            ...filter,
            fromDate: fixStartDate(filter?.fromDate),
            toDate: fixEndDate(filter?.fromDate),
            sort: {
                property: "date",
                direction: SortDirection.Descending
            },
            pagination: {
                offset,
                limit
            },
            saleCircuitId: context.saleCircuit.id
        }),

        openItem: item => {
            navigatePage("order-details", { id: item.id })
        },

        itemView: () => ({
            maxActions: 2,
            style: ["round-icon"],
        }),

        itemActions: (item, result) => {
        },

        emptyView: <EmptyView
            iconName="credit_card"
            message="msg-no-orders"
        />,

        columns: [{
            value: a => formatOrderType(a.type),
            priority: "primary",
            header: "type",
        }, {
            value: a => formatDateTime(a.date),
            priority: "secondary",
            header: "date",
        }, {
            value: a => a.salePoint?.name,
            priority: "secondary",
            header: "sale-point",
        }, {
            value: a => a.merchant?.name,
            priority: "secondary",
            header: "merchant",
        }, {
            value: a => formatCurrency(a.total),
            priority: "evidence",
            header: "total",
        },
        ]

    }).asContent();